'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import BookmarkButton from '@/components/BookmarkButton';

const rings = [
  { r: 240, labels: ['AMR', 'DHATI', 'HUKUM', 'SUNNAH', 'TAQDEER', 'MIZAN', 'FITRA', 'AMANAH'], opacity: 0.06 },
  { r: 180, labels: ['AR-RAHMAN', 'AL-KHALIQ', 'AL-ALIM', 'AL-HAKIM', 'AL-NUR', 'AL-MALIK', 'AL-HAFIZ', 'AL-ADL'], opacity: 0.1 },
  { r: 120, labels: ['AYAT', 'HAAL', 'TAQWAH', 'MIZAN', 'AMANAH'], opacity: 0.15 },
];

const keyPills = [
  { ar: 'آيات', en: 'AYAT' },
  { ar: 'حال', en: 'HAAL' },
  { ar: 'تقوى', en: 'TAQWAH' },
  { ar: 'ميزان', en: 'MIZAN' },
  { ar: 'أمانة', en: 'AMANAH' },
];

// Clickable verse link component
function VerseLink({ ref: verseRef, href }: { ref: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 transition-all duration-200 border-b"
      style={{ color: 'rgba(196,149,106,0.85)', borderColor: 'rgba(196,149,106,0.3)', fontSize: 'inherit' }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#D4A82A'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212,168,42,0.6)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.85)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(196,149,106,0.3)'; }}
    >
      <span>{verseRef}</span>
      <span style={{ fontSize: '0.7em', opacity: 0.6 }}>↗</span>
    </a>
  );
}

export default function KeysHero() {
  const [loaded, setLoaded] = useState(false);
  const [activePill, setActivePill] = useState(0);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    const interval = setInterval(() => setActivePill(p => (p + 1) % 5), 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay pt-20">
      {/* Cursor glow */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 30%, rgba(139,105,20,0.08) 0%, transparent 60%)' }} />
      {/* Gold pixel border */}
      <div className="pixel-border-gold-top" />
      {/* Background grid — gold tinted */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
      {/* Concentric ring diagram */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="-280 -280 560 560" width="700" height="700" className="opacity-30">
          {rings?.map((ring, ri) => (
            <g key={ri}>
              <circle cx="0" cy="0" r={ring?.r} fill="none" stroke="rgba(179,138,42,0.6)" strokeWidth="0.5" opacity={ring?.opacity * 5} />
              {ring?.labels?.map((label, li) => {
                const angle = (li / ring?.labels?.length) * 360 - 90;
                const rad = (angle * Math.PI) / 180;
                const x = (ring?.r - 20) * Math.cos(rad);
                const y = (ring?.r - 20) * Math.sin(rad);
                return (
                  <text key={li} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fill="rgba(245,240,232,0.9)" fontSize="7" fontFamily="monospace" opacity={ring?.opacity * 8} letterSpacing="1">
                    {label}
                  </text>
                );
              })}
            </g>
          ))}
          {[0, 45, 90, 135]?.map((angle) => (
            <line key={angle}
              x1={-260 * Math.cos((angle * Math.PI) / 180)} y1={-260 * Math.sin((angle * Math.PI) / 180)}
              x2={260 * Math.cos((angle * Math.PI) / 180)} y2={260 * Math.sin((angle * Math.PI) / 180)}
              stroke="rgba(139,105,20,0.15)" strokeWidth="0.5" opacity="1"
            />
          ))}
          {[0, 72, 144, 216, 288]?.map((angle, i) => {
            const rad = ((angle - 90) * Math.PI) / 180;
            return (
              <line key={i}
                x1={55 * Math.cos(rad)} y1={55 * Math.sin(rad)}
                x2={115 * Math.cos(rad)} y2={115 * Math.sin(rad)}
                stroke="rgba(179,138,42,0.4)" strokeWidth="0.3" opacity="1"
              />
            );
          })}
          {/* Center disc */}
          <circle cx="0" cy="0" r="55" fill="#1C2B25" stroke="rgba(179,138,42,0.6)" strokeWidth="1" opacity="0.9" />
          <circle cx="0" cy="0" r="45" fill="none" stroke="rgba(139,105,20,0.3)" strokeWidth="0.3" opacity="1" />
          <text x="0" y="-8" textAnchor="middle" fill="#F5F0E8" fontSize="16" fontFamily="monospace" fontWeight="900" opacity="0.9">AYA</text>
          <text x="0" y="10" textAnchor="middle" fill="rgba(179,138,42,0.7)" fontSize="6" fontFamily="monospace" letterSpacing="2" opacity="1">KULLI HAAL</text>
        </svg>
      </div>
      {/* Content */}
      <div className={`relative z-10 max-w-[900px] mx-auto px-6 text-center transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

        {/* kaif haal? Alhamdulilah greeting */}
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.5)' }}>كَيْفَ حَالُكَ؟</span>
          <span style={{ color: 'rgba(139,105,20,0.3)', fontSize: 10 }}>·</span>
          <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(196,149,106,0.6)' }}>الْحَمْدُ لِلَّه عَلَى كُلِّ حَال</span>
        </div>
        <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(139,105,20,0.5)' }}>
          <em style={{ fontStyle: 'normal', color: 'rgba(196,149,106,0.5)' }}>kaif haal?</em>
          <span style={{ color: 'rgba(139,105,20,0.3)', margin: '0 6px' }}>·</span>
          <em style={{ fontStyle: 'normal', color: 'rgba(196,149,106,0.7)' }}>Alhamdulilah ala kulli haal</em>
        </p>
        <p className="font-mono text-[9px] tracking-widest uppercase mb-6" style={{ color: 'rgba(139,105,20,0.4)' }}>01 — Curriculum</p>

        <h1 className="font-mono text-5xl md:text-7xl font-black uppercase tracking-tight leading-none mb-4" style={{ color: '#F5F0E8' }}>
          KEYS TO<br />
          <span className="text-shimmer">KULLI HAAL</span>
        </h1>

        {/* Ihdini — comprehensive guidance concept */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(to right, transparent, rgba(139,105,20,0.3))' }} />
          <div className="text-center">
            <span style={{ fontFamily: 'serif', fontSize: 18, color: 'rgba(196,149,106,0.8)', direction: 'rtl', display: 'block' }}>اهْدِنَا الصِّرَاطَ الْمُسْتَقِيم</span>
            <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.5)' }}>Ihdina — Guide us to the straight path</span>
          </div>
          <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(to left, transparent, rgba(139,105,20,0.3))' }} />
        </div>

        <p className="font-mono text-sm max-w-lg mx-auto leading-relaxed mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>
          A hand-held walk through the fabric of reality. Five keys that unlock the curriculum — each one a layer of the Inner Reality Lens, each one pointing back to the One.
        </p>

        {/* Ihdini as the guiding concept note */}
        <p className="font-mono text-[10px] max-w-md mx-auto leading-relaxed mb-8" style={{ color: 'rgba(245,240,232,0.3)' }}>
          Ihdini (اهدني) — the most repeated du&apos;a in the Quran — is the comprehensive guidance concept that holds all five keys together. The Keys are not a curriculum to master; they are a path to walk, hand-in-hand with the Guide.
        </p>

        {/* Animated key pills */}
        <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
          {keyPills?.map((pill, i) => (
            <div
              key={pill?.en}
              className="border px-3 py-1.5 transition-all duration-500"
              style={{
                borderColor: activePill === i ? 'rgba(179,138,42,0.7)' : 'rgba(44,74,62,0.5)',
                background: activePill === i ? 'rgba(139,105,20,0.15)' : 'transparent',
              }}
            >
              <span className="font-mono text-[10px] tracking-widest uppercase transition-colors duration-500"
                style={{ color: activePill === i ? '#D4A82A' : 'rgba(245,240,232,0.25)' }}
              >
                {pill?.en}
              </span>
            </div>
          ))}
        </div>

        {/* Al-Asr foundational verse */}
        <div className="p-5 max-w-xl mx-auto mb-6" style={{ border: '1px solid rgba(139,105,20,0.25)', background: 'rgba(28,43,37,0.5)' }}>
          <p className="font-mono text-[9px] tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.5)' }}>Foundational Verse</p>
          <p style={{ fontFamily: 'serif', fontSize: 16, color: 'rgba(196,149,106,0.8)', direction: 'rtl', lineHeight: 1.8, marginBottom: 8 }}>
            وَالْعَصْرِ ۝ إِنَّ الْإِنسَانَ لَفِي خُسْرٍ ۝ إِلَّا الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ وَتَوَاصَوْا بِالْحَقِّ وَتَوَاصَوْا بِالصَّبْرِ
          </p>
          <p className="font-mono text-[10px] leading-relaxed mb-3" style={{ color: 'rgba(245,240,232,0.45)' }}>
            &ldquo;By time — indeed, mankind is in loss, except those who believe and do righteous deeds and advise each other to truth and advise each other to patience.&rdquo;
          </p>
          <div className="flex items-center justify-between">
            <VerseLink ref="Quran 103 · Al-Asr" href="https://quran.com/103" />
            <span className="font-mono text-[9px]" style={{ color: 'rgba(139,105,20,0.4)' }}>The framework for every key</span>
          </div>
          <p className="font-mono text-[9px] leading-relaxed mt-3" style={{ color: 'rgba(245,240,232,0.25)' }}>
            Al-Asr is the foundational verse of the Keys framework: Iman (Ayat) + Amal Salih (Amanah) + Tawasi bil-Haqq (Mizan) + Tawasi bis-Sabr (Taqwah). The four conditions of Falah — cultivation, not accumulation.
          </p>
        </div>

        {/* IRL Lens connection note */}
        <div className="p-4 max-w-md mx-auto mb-10" style={{ border: '1px solid rgba(44,74,62,0.4)', background: 'rgba(28,43,37,0.4)' }}>
          <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(139,105,20,0.5)' }}>Connected to</p>
          <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Each key is a dimension of the{' '}
            <Link href="/inner-reality-lens" className="transition-colors border-b" style={{ color: 'rgba(196,149,106,0.8)', borderColor: 'rgba(196,149,106,0.3)' }}>
              Inner Reality Lens
            </Link>
            {' '}— the methodological instrument for recognizing Ayat in creation.
          </p>
        </div>

        <div className="flex items-center justify-center gap-6">
          <Link href="#curriculum" className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
          >
            Explore Keys
          </Link>
          <Link href="/bridge-environment-design" className="font-mono text-xs tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.4)', borderColor: 'rgba(139,105,20,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
          >
            Next: Bridge 01 →
          </Link>
          <BookmarkButton
            item={{
              item_type: 'framework_page',
              item_id: 'keys-to-kulli-haal',
              item_title: 'Keys to Kulli Haal',
              item_arabic: 'مَفَاتِيح',
              item_href: '/keys-to-kulli-haal',
              item_description: 'Seven conceptual frameworks — Ayat, Haal, Taqwah, Mizan, Amanah, Falah, Yaqeen',
              item_category: 'framework',
            }}
            size="md"
            showLabel
          />
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ opacity: 0.35 }}>
        <span className="font-mono text-xs tracking-widest uppercase" style={{ color: '#F5F0E8' }}>Scroll</span>
        <div className="w-px h-10" style={{ background: 'linear-gradient(to bottom, rgba(179,138,42,0.6), transparent)' }} />
      </div>
    </section>
  );
}
